import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  ColorPicker,
  ConfigProvider,
  Input,
  Menu,
  Popconfirm,
  Radio,
  Result,
  Skeleton,
  Space,
  Steps,
  Switch,
  Typography,
  message,
} from 'antd'
import { SaveOutlined, UndoOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import B2BLayout from '../../../layouts/b2b'
import fetchAPI, { IHttpError } from '../../../../lib/utils/fetch-api'
import { getLoadSessionAction } from '../../../store/session/action-creators'
import { useAppDispatch } from '../../../hooks/useAppDispatch/useAppDispatch'
import {
  DEFAULT_SYSTEM_THEME,
  SYSTEM_THEME_COMPONENT_KEYS,
  SystemThemeComponentKey,
  SystemThemePayload,
  createAntdTheme,
  isHexColor,
  normalizeSystemTheme,
  resolveSystemTheme,
} from '../../../styles/system-theme'

type DraftSystemTheme = {
  primary: string
  components: Record<SystemThemeComponentKey, string | null>
}

type ColorFieldProps = {
  label: string
  description?: string
  value: string | null
  fallback: string
  allowFallback?: boolean
  onChange: (value: string | null) => void
}

const breadcrumbs = [
  {
    href: '/admin',
    title: 'Админка',
  },
  {
    href: '/admin/system-theme',
    title: 'Цвета системы',
  },
]

const componentLabels: Record<SystemThemeComponentKey, { title: string; description: string }> = {
  button: {
    title: 'Кнопки',
    description: 'Primary-кнопки и основные действия.',
  },
  checkbox: {
    title: 'Чекбоксы, radio, switch',
    description: 'Выбранные состояния бинарных контролов.',
  },
  steps: {
    title: 'Стадии AntD Steps',
    description: 'Иконки текущих и завершенных шагов.',
  },
  activeMenu: {
    title: 'Активное меню',
    description: 'Выбранный пункт бокового меню.',
  },
  focus: {
    title: 'Фокус и hover',
    description: 'Обводки полей, hover-состояния и focus ring.',
  },
}

const PageGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(360px, 520px) minmax(420px, 1fr);
  gap: 24px;

  @media (max-width: 1180px) {
    grid-template-columns: 1fr;
  }
`

const SettingsColumn = styled.div`
  display: grid;
  gap: 16px;
  align-content: start;
`

const Section = styled.section`
  border: 1px solid #e6e6e6;
  border-radius: 8px;
  background: #ffffff;
  padding: 20px;
`

const SectionTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 16px;
  line-height: 22px;
`

const SectionDescription = styled.p`
  margin: 0 0 16px;
  color: rgba(0, 0, 0, 0.55);
`

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: minmax(170px, 1fr) minmax(220px, 260px);
  gap: 16px;
  align-items: center;
  padding: 14px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`

const FieldLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const FieldDescription = styled.span`
  color: rgba(0, 0, 0, 0.55);
  font-size: 13px;
`

const ColorControls = styled.div`
  display: grid;
  grid-template-columns: 42px 1fr;
  gap: 8px;
  align-items: center;
`

const ComponentControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const PreviewSurface = styled.div`
  display: grid;
  gap: 20px;
`

const PreviewBlock = styled.div`
  border-top: 1px solid #f0f0f0;
  padding-top: 16px;

  &:first-child {
    border-top: none;
    padding-top: 0;
  }
`

const PreviewBlockTitle = styled.div`
  margin-bottom: 10px;
  font-weight: 600;
`

const PreviewMenu = styled.div`
  width: 260px;
  overflow: hidden;
  border-radius: 8px;
`

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
`

const getColorFromPicker = (color: any, cssColor?: string) => {
  if (typeof cssColor === 'string' && isHexColor(cssColor)) {
    return cssColor
  }
  if (typeof color?.toHexString === 'function') {
    return color.toHexString()
  }
  return ''
}

const ColorField = ({
  label,
  description,
  value,
  fallback,
  allowFallback = false,
  onChange,
}: ColorFieldProps) => {
  const useFallback = allowFallback && value === null
  const resolvedValue = useFallback ? fallback : value || fallback
  const hasError = !useFallback && !isHexColor(resolvedValue)

  return (
    <FieldRow>
      <FieldLabel>
        <strong>{label}</strong>
        {description ? <FieldDescription>{description}</FieldDescription> : null}
      </FieldLabel>
      <ComponentControls>
        {allowFallback ? (
          <Checkbox
            checked={useFallback}
            onChange={event => onChange(event.target.checked ? null : fallback)}
          >
            Использовать общий цвет
          </Checkbox>
        ) : null}
        <ColorControls>
          <ColorPicker
            value={resolvedValue}
            disabled={useFallback}
            format="hex"
            disabledAlpha
            onChange={(color, cssColor) => onChange(getColorFromPicker(color, cssColor))}
          />
          <Input
            value={resolvedValue}
            disabled={useFallback}
            status={hasError ? 'error' : undefined}
            onChange={event => onChange(event.target.value)}
          />
        </ColorControls>
      </ComponentControls>
    </FieldRow>
  )
}

const validateTheme = (theme: DraftSystemTheme) => {
  const errors: string[] = []

  if (!isHexColor(theme.primary)) {
    errors.push('Укажите корректный общий цвет')
  }

  SYSTEM_THEME_COMPONENT_KEYS.forEach(key => {
    const value = theme.components[key]
    if (value !== null && !isHexColor(value)) {
      errors.push(`Укажите корректный цвет: ${componentLabels[key].title}`)
    }
  })

  return errors
}

export const SystemThemePage = () => {
  const dispatch = useAppDispatch()
  const [theme, setTheme] = useState<DraftSystemTheme>(DEFAULT_SYSTEM_THEME)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<IHttpError | null>(null)

  const validationErrors = useMemo(() => validateTheme(theme), [theme])
  const normalizedTheme = useMemo(() => normalizeSystemTheme(theme as SystemThemePayload), [theme])
  const resolvedTheme = useMemo(() => resolveSystemTheme(normalizedTheme), [normalizedTheme])
  const previewAntdTheme = useMemo(() => createAntdTheme(resolvedTheme), [resolvedTheme])

  const loadTheme = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await fetchAPI('/api/admin/system-theme')
      setTheme(normalizeSystemTheme(response?.data as SystemThemePayload))
    } catch (err) {
      setError(err as IHttpError)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadTheme()
  }, [loadTheme])

  const updatePrimary = useCallback((value: string | null) => {
    if (value === null) return
    setTheme(prev => ({ ...prev, primary: value }))
  }, [])

  const updateComponent = useCallback((key: SystemThemeComponentKey, value: string | null) => {
    setTheme(prev => ({
      ...prev,
      components: {
        ...prev.components,
        [key]: value,
      },
    }))
  }, [])

  const resetTheme = useCallback(() => {
    setTheme(DEFAULT_SYSTEM_THEME)
  }, [])

  const handleSave = useCallback(async () => {
    const errors = validateTheme(theme)
    if (errors.length > 0) {
      message.error(errors[0])
      return
    }

    try {
      setSaving(true)
      const payload = normalizeSystemTheme(theme as SystemThemePayload)
      const response = await fetchAPI('/api/admin/system-theme', {
        method: 'POST',
        body: JSON.stringify(payload),
      })

      if (response?.errors) {
        message.error(Array.isArray(response.errors) ? response.errors[0] : response.errors)
        return
      }

      setTheme(normalizeSystemTheme(response?.data as SystemThemePayload))
      await dispatch(getLoadSessionAction())
      message.success('Цвета системы сохранены')
    } catch (err: any) {
      message.error(err?.message || 'Не удалось сохранить цвета системы')
    } finally {
      setSaving(false)
    }
  }, [dispatch, theme])

  return (
    <B2BLayout breadcrumbs={breadcrumbs} pageTitle="Цвета системы">
      {loading && <Skeleton active />}
      {!loading && error && (
        <Result
          status={(error.statusCode || 'error') as any}
          title="Ошибка"
          subTitle={error.message}
        />
      )}
      {!loading && !error && (
        <>
          <PageGrid>
            <SettingsColumn>
              <Section>
                <SectionTitle>Общий цвет</SectionTitle>
                <SectionDescription>
                  Цвет по умолчанию. Он применяется ко всем элементам, для которых ниже не задан
                  отдельный цвет.
                </SectionDescription>
                <ColorField
                  label="Основной цвет"
                  description="Базовый цвет системы"
                  value={theme.primary}
                  fallback={DEFAULT_SYSTEM_THEME.primary}
                  onChange={updatePrimary}
                />
              </Section>

              <Section>
                <SectionTitle>Индивидуальные цвета</SectionTitle>
                <SectionDescription>
                  Здесь можно переопределить цвет отдельных элементов. Если включен общий цвет,
                  элемент будет использовать цвет по умолчанию.
                </SectionDescription>
                {SYSTEM_THEME_COMPONENT_KEYS.map(key => (
                  <ColorField
                    key={key}
                    label={componentLabels[key].title}
                    description={componentLabels[key].description}
                    value={theme.components[key]}
                    fallback={theme.primary}
                    allowFallback
                    onChange={value => updateComponent(key, value)}
                  />
                ))}
              </Section>
            </SettingsColumn>

            <Section>
              <SectionTitle>Предпросмотр</SectionTitle>
              <SectionDescription>
                Изменения в предпросмотре видны сразу. Вся система применит тему после сохранения.
              </SectionDescription>

              {validationErrors.length > 0 ? (
                <Alert
                  type="warning"
                  showIcon
                  message={validationErrors[0]}
                  style={{ marginBottom: 16 }}
                />
              ) : null}

              <ConfigProvider theme={previewAntdTheme}>
                <PreviewSurface>
                  <PreviewBlock>
                    <PreviewBlockTitle>Кнопки и контролы</PreviewBlockTitle>
                    <Space wrap>
                      <Button type="primary">Основное действие</Button>
                      <Button>Обычная кнопка</Button>
                      <Checkbox checked>Чекбокс</Checkbox>
                      <Radio checked>Radio</Radio>
                      <Switch checked />
                    </Space>
                  </PreviewBlock>

                  <PreviewBlock>
                    <PreviewBlockTitle>Стадии</PreviewBlockTitle>
                    <Steps
                      current={1}
                      size="small"
                      items={[{ title: 'Создано' }, { title: 'В работе' }, { title: 'Готово' }]}
                    />
                  </PreviewBlock>

                  <PreviewBlock>
                    <PreviewBlockTitle>Активное меню</PreviewBlockTitle>
                    <PreviewMenu>
                      <Menu
                        theme="dark"
                        mode="inline"
                        selectedKeys={['theme']}
                        items={[
                          { key: 'collections', label: 'Справочники' },
                          { key: 'theme', label: 'Цвета системы' },
                          { key: 'roles', label: 'Роли' },
                        ]}
                      />
                    </PreviewMenu>
                  </PreviewBlock>

                  <PreviewBlock>
                    <PreviewBlockTitle>Фокус</PreviewBlockTitle>
                    <Input placeholder="Поле ввода" style={{ maxWidth: 360 }} />
                  </PreviewBlock>
                </PreviewSurface>
              </ConfigProvider>
            </Section>
          </PageGrid>

          <FooterActions>
            <Popconfirm
              title="Сбросить цвета к стандартным?"
              okText="Да"
              cancelText="Нет"
              onConfirm={resetTheme}
            >
              <Button icon={<UndoOutlined />}>Сбросить к стандартным</Button>
            </Popconfirm>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
              disabled={validationErrors.length > 0}
            >
              Сохранить
            </Button>
          </FooterActions>

          <Typography.Text type="secondary">
            Если для элемента выбран общий цвет, отдельное значение не сохраняется.
          </Typography.Text>
        </>
      )}
    </B2BLayout>
  )
}

export default SystemThemePage
